import React, { useState, useEffect, useCallback, forwardRef } from "react";
import { Loader2, CheckCircle2, ClipboardList } from "lucide-react";
import { publicClient } from "@/lib/apiClient";
import { RESIDENTIAL_PLANS, COMMERCIAL_PLANS } from "@/data/maintenancePlans";

const PROPERTY_TYPES = [
  "Single-family home",
  "Townhome",
  "Condo",
  "Duplex / multi-family",
  "Investment property",
  "Commercial / mixed-use",
];

const CONTACT_METHODS = ["Phone call", "Text", "Email"];

const ALL_PLANS_FOR_SELECT = [
  ...RESIDENTIAL_PLANS.map((p) => ({ name: p.name, group: "Residential" })),
  ...COMMERCIAL_PLANS.map((p) => ({ name: p.name, group: "Commercial / Portfolio" })),
];

const INITIAL = {
  name: "",
  phone: "",
  email: "",
  address: "",
  city: "",
  property_type: "Single-family home",
  sqft: "",
  plan: "",
  known_issues: "",
  preferred_contact: "Phone call",
  message: "",
  // Snapshot of calculator output (read-only)
  monthly_price: "",
  inspection_fee: "",
  crew_hours: "",
  annual_allowance: "",
  // Honeypot
  website: "",
};

/**
 * Builds a structured notes block out of all fields that the backend
 * MaintenanceSignup schema doesn't carry as its own column. This keeps the
 * record useful inside the /admin dashboard without a backend migration.
 */
function buildNotesBlock(form) {
  const lines = [];
  if (form.city) lines.push(`City / service area: ${form.city}`);
  if (form.sqft) lines.push(`Home square footage: ${form.sqft}`);
  if (form.known_issues) lines.push(`Current known issues: ${form.known_issues}`);
  if (form.preferred_contact) lines.push(`Preferred contact method: ${form.preferred_contact}`);
  if (form.monthly_price) lines.push(`Estimated monthly (from calculator): ${form.monthly_price}`);
  if (form.inspection_fee) lines.push(`Initial inspection fee: ${form.inspection_fee}`);
  if (form.crew_hours) lines.push(`Included crew hours per covered claim: ${form.crew_hours}`);
  if (form.annual_allowance) lines.push(`Annual covered-work allowance: ${form.annual_allowance}`);
  if (form.message) {
    lines.push("");
    lines.push("Message from homeowner:");
    lines.push(form.message);
  }
  return lines.join("\n");
}

/**
 * MembershipRequestForm
 *
 * Dedicated maintenance membership request form for the bottom of the
 * Maintenance Plans page. Accepts a `prefill` object so the page can
 * pre-populate `sqft`, `plan`, and the calculator snapshot when the
 * homeowner uses the calculator or clicks a plan card CTA.
 *
 * Submits to POST /api/maintenance-signups so submissions show up in the
 * existing /admin "Maintenance Signups" tab.
 */
const MembershipRequestForm = forwardRef(function MembershipRequestForm({ prefill }, ref) {
  const [form, setForm] = useState(INITIAL);
  const [state, setState] = useState({ loading: false, success: false, error: "" });

  // Apply prefill from parent (calculator / plan card) whenever it changes.
  useEffect(() => {
    if (!prefill) return;
    setForm((prev) => ({
      ...prev,
      ...(prefill.plan ? { plan: prefill.plan } : {}),
      ...(prefill.sqft ? { sqft: String(prefill.sqft) } : {}),
      ...(prefill.monthly_price ? { monthly_price: prefill.monthly_price } : {}),
      ...(prefill.inspection_fee ? { inspection_fee: prefill.inspection_fee } : {}),
      ...(prefill.crew_hours ? { crew_hours: prefill.crew_hours } : {}),
      ...(prefill.annual_allowance ? { annual_allowance: prefill.annual_allowance } : {}),
    }));
    // Reset any prior success message when a new selection is applied.
    setState((s) => (s.success ? { loading: false, success: false, error: "" } : s));
  }, [prefill]);

  const update = useCallback((k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value })), []);

  const submit = useCallback(async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email || !form.address || !form.plan) {
      setState({ loading: false, success: false, error: "Please fill in name, phone, email, property address, and plan." });
      return;
    }
    setState({ loading: true, success: false, error: "" });
    try {
      const payload = {
        name: form.name,
        phone: form.phone,
        email: form.email,
        address: form.address,
        plan: form.plan,
        property_type: form.property_type || "residential",
        notes: buildNotesBlock(form),
        website: form.website,
      };
      await publicClient.post("/maintenance-signups", payload);
      setState({ loading: false, success: true, error: "" });
    } catch (err) {
      setState({
        loading: false,
        success: false,
        error: err?.response?.data?.detail || "Something went wrong, please try again.",
      });
    }
  }, [form]);

  return (
    <div
      ref={ref}
      id="request-details"
      className="bg-white border border-gray-200 rounded-lg overflow-hidden scroll-mt-24"
      data-testid="membership-request-form"
    >
      <div className="px-6 py-5 border-b border-gray-200 bg-br-paper flex items-start gap-3">
        <ClipboardList size={20} className="text-br-bronze shrink-0 mt-1" />
        <div>
          <div className="br-eyebrow text-br-bronze">Get Started</div>
          <h3 className="font-display text-2xl tracking-widest uppercase mt-1">Request Maintenance Membership Details</h3>
          <p className="text-sm text-gray-700 mt-2 max-w-2xl">
            Tell us about your property and the plan you&rsquo;re interested in. We&rsquo;ll review your information, confirm pricing after a brief property review, and follow up with next steps including your baseline inspection.
          </p>
        </div>
      </div>

      {state.success ? (
        <div className="p-6" data-testid="membership-request-success">
          <div className="flex items-start gap-3 bg-br-paper border border-br-bronze rounded p-5">
            <CheckCircle2 size={22} className="text-br-bronze shrink-0 mt-0.5" />
            <div>
              <div className="font-display tracking-widest uppercase text-lg text-br-black">Request Received</div>
              <p className="text-sm text-gray-700 mt-1 leading-relaxed">
                Thank you for your interest in a Black Ridge Construction maintenance membership. We received your request and will follow up with next steps.
              </p>
            </div>
          </div>
        </div>
      ) : (
        <form onSubmit={submit} className="p-6 grid sm:grid-cols-2 gap-4" data-testid="membership-request-fields">
          <div>
            <label className="label-br" htmlFor="mrf-name">Name *</label>
            <input id="mrf-name" className="input-br" value={form.name} onChange={update("name")} required data-testid="mrf-name" />
          </div>
          <div>
            <label className="label-br" htmlFor="mrf-phone">Phone *</label>
            <input id="mrf-phone" className="input-br" type="tel" value={form.phone} onChange={update("phone")} required data-testid="mrf-phone" />
          </div>
          <div>
            <label className="label-br" htmlFor="mrf-email">Email *</label>
            <input id="mrf-email" className="input-br" type="email" value={form.email} onChange={update("email")} required data-testid="mrf-email" />
          </div>
          <div>
            <label className="label-br" htmlFor="mrf-address">Property Address *</label>
            <input id="mrf-address" className="input-br" value={form.address} onChange={update("address")} required data-testid="mrf-address" />
          </div>
          <div>
            <label className="label-br" htmlFor="mrf-city">City / Service Area</label>
            <input id="mrf-city" className="input-br" placeholder="Austin, Round Rock, Cedar Park..." value={form.city} onChange={update("city")} data-testid="mrf-city" />
          </div>
          <div>
            <label className="label-br" htmlFor="mrf-property-type">Property Type</label>
            <select id="mrf-property-type" className="select-br" value={form.property_type} onChange={update("property_type")} data-testid="mrf-property-type">
              {PROPERTY_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <label className="label-br" htmlFor="mrf-sqft">Home Square Footage</label>
            <input id="mrf-sqft" className="input-br" type="number" inputMode="numeric" min="100" max="20000" placeholder="e.g. 2100" value={form.sqft} onChange={update("sqft")} data-testid="mrf-sqft" />
          </div>
          <div>
            <label className="label-br" htmlFor="mrf-plan">Plan Interested In *</label>
            <select id="mrf-plan" className="select-br" value={form.plan} onChange={update("plan")} required data-testid="mrf-plan">
              <option value="">Select a plan…</option>
              <optgroup label="Residential">
                {ALL_PLANS_FOR_SELECT.filter((p) => p.group === "Residential").map((p) => (
                  <option key={p.name} value={p.name}>{p.name}</option>
                ))}
              </optgroup>
              <optgroup label="Commercial / Portfolio">
                {ALL_PLANS_FOR_SELECT.filter((p) => p.group === "Commercial / Portfolio").map((p) => (
                  <option key={p.name} value={p.name}>{p.name}</option>
                ))}
              </optgroup>
              <option value="Not sure yet">Not sure yet</option>
            </select>
          </div>
          <div className="sm:col-span-2">
            <label className="label-br" htmlFor="mrf-known-issues">Are there current known issues?</label>
            <textarea id="mrf-known-issues" rows={2} className="textarea-br" placeholder="Leaks, deferred maintenance, recent storm damage, etc. (optional)" value={form.known_issues} onChange={update("known_issues")} data-testid="mrf-known-issues" />
          </div>
          <div>
            <label className="label-br" htmlFor="mrf-preferred-contact">Preferred Contact Method</label>
            <select id="mrf-preferred-contact" className="select-br" value={form.preferred_contact} onChange={update("preferred_contact")} data-testid="mrf-preferred-contact">
              {CONTACT_METHODS.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
          <div>
            <label className="label-br" htmlFor="mrf-monthly-price">Estimated Monthly (from calculator)</label>
            <input
              id="mrf-monthly-price"
              className="input-br bg-gray-50"
              value={form.monthly_price}
              onChange={update("monthly_price")}
              placeholder="Auto-filled if you used the calculator"
              data-testid="mrf-monthly-price"
              readOnly
            />
          </div>
          <div className="sm:col-span-2">
            <label className="label-br" htmlFor="mrf-message">Anything else?</label>
            <textarea id="mrf-message" rows={3} className="textarea-br" placeholder="Optional notes for the Black Ridge team." value={form.message} onChange={update("message")} data-testid="mrf-message" />
          </div>

          {/* Honeypot */}
          <input
            type="text"
            tabIndex={-1}
            autoComplete="off"
            value={form.website}
            onChange={update("website")}
            className="hidden"
            aria-hidden="true"
          />

          {state.error && (
            <div className="sm:col-span-2 text-sm text-red-600" data-testid="mrf-error">{state.error}</div>
          )}

          <div className="sm:col-span-2 flex flex-wrap items-center gap-3 pt-1">
            <button type="submit" disabled={state.loading} className="br-btn-primary" data-testid="mrf-submit">
              {state.loading ? <><Loader2 className="animate-spin" size={14} /> Sending…</> : "Request Membership Details"}
            </button>
            <span className="text-[11px] text-gray-500 italic">
              Final monthly pricing and initial inspection fee are confirmed after a brief property review.
            </span>
          </div>
        </form>
      )}
    </div>
  );
});

export default MembershipRequestForm;
